import { Controller } from "@hotwired/stimulus"
import { createRoot } from "react-dom/client"
import { createElement } from "react"
import { ClerkAuth } from "@/components/clerkAuth"

export default class extends Controller {
  static values = {
    publishableKey: String,
    redirectUrl: String,
    subtitle: String,
    showSpinner: { type: Boolean, default: true }
  }

  // Server-rendered spinner that sits BEHIND the Clerk form and stays there for
  // the lifetime of the page. Mark it with data-clerk-auth-target="placeholder".
  static targets = ["placeholder"]

  connect() {
    // When a server-rendered placeholder exists, keep it on screen and mount
    // React into a SEPARATE node so the placeholder isn't wiped on mount. The
    // node starts faded out and crossfades in once Clerk reports loaded — so
    // it's server spinner → Clerk form, with no empty gap or double spinner.
    const usePlaceholder = this.hasPlaceholderTarget

    this.mount = document.createElement("div")
    if (usePlaceholder) {
      // Stack the Clerk form above the placeholder spinner so the spinner sits
      // behind it — and shows through when Clerk tears the form down to redirect.
      this.mount.classList.add("relative", "z-10", "transition-opacity", "duration-300", "opacity-0")
    }
    this.element.appendChild(this.mount)

    this.root = createRoot(this.mount)
    this.root.render(
      createElement(ClerkAuth, {
        publishableKey: this.publishableKeyValue,
        redirectUrl: this.redirectUrlValue,
        subtitle: this.subtitleValue || undefined,
        // The server placeholder covers the pre-load state, so skip React's own.
        showSpinner: usePlaceholder ? false : this.showSpinnerValue,
        onLoaded: usePlaceholder ? () => this.revealForm() : undefined
      })
    )
  }

  // Fade the Clerk form in over the placeholder spinner. The placeholder stays
  // mounted behind the form on purpose: once Clerk authenticates it clears its
  // form to redirect, and keeping the spinner underneath means the user sees a
  // spinner during that wait instead of a blank gap and wondering what to do.
  revealForm() {
    this.mount.classList.remove("opacity-0")
  }

  disconnect() {
    if (this.root) {
      this.root.unmount()
    }
  }
}